export interface TurnstileErrorProps {
  'Error Code'?: string;
  'Error Message'?: string;
}

// https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
const TURNSTILE_ERROR_MESSAGES: { [key: string]: string } = {
  INIT_ERROR: `Initialization Problems: There was a problem initializing Turnstile before a challenge could be started.`,
  INVALID_PARAM: `Invalid Parameters: The visitor sent an invalid parameter as part of the challenge towards Turnstile.`,
  API_INCOMPATIBLE: `Turnstile API Compatibility: Turnstile was invoked in a deprecated or invalid way.`,
  CHALLENGE_TIMEOUT: `Challenge timed out: The visitor took too long to solve the challenge and the challenge timed out.`,
  GENERIC_CLIENT_ERROR: `Generic client execution error: An unspecified error occurred in the visitor while they were solving a challenge.`,
  CHALLENGE_FAILURE: `Challenge execution failure: A visitor failed to solve a Turnstile Challenge. Also used by failing testing sitekey.`,
  INVALID_SITEKEY: `Invalid sitekey: Turnstile was invoked with an invalid sitekey or a sitekey that is no longer active.`,
  INVALID_DOMAIN: 'Unknown domain: Domain not allowed.',
  INVALID_ACTION: `Invalid action: This error occurs when an unsupported or incorrectly formatted action is submitted.`,
  INVALID_CDATA: `Invalid cData: This error in Turnstile refers to an issue encountered when processing Custom Data (cData). This error occurs when the cData provided does not adhere to the expected format or contains invalid characters.`,
  UNSUPPORTED_BROWSER: 'Unsupported browser: The visitor is using an unsupported browser.',
  INCONSISTENT_USER_AGENT: `Inconsistent user-agent: The visitor provided an inconsistent user-agent throughout the process of solving Turnstile.`,
  INVALID_CACHING: 'Invalid caching: Some portion of Turnstile was accidentally cached.',
  TIME_ERROR: `Time problem: The visitor's clock is incorrect.`
};

const ERROR_MESSAGES_TO_PREFIXES_MAP: { [key: string]: string } = {
  '100': TURNSTILE_ERROR_MESSAGES.INIT_ERROR,
  '102': TURNSTILE_ERROR_MESSAGES.INVALID_PARAM,
  '103': TURNSTILE_ERROR_MESSAGES.INVALID_PARAM,
  '104': TURNSTILE_ERROR_MESSAGES.INVALID_PARAM,
  '106': TURNSTILE_ERROR_MESSAGES.INVALID_PARAM,
  '105': TURNSTILE_ERROR_MESSAGES.API_INCOMPATIBLE,
  '300': TURNSTILE_ERROR_MESSAGES.GENERIC_CLIENT_ERROR,
  '600': TURNSTILE_ERROR_MESSAGES.CHALLENGE_FAILURE,
  '11060': TURNSTILE_ERROR_MESSAGES.CHALLENGE_TIMEOUT,
  '110100': TURNSTILE_ERROR_MESSAGES.INVALID_SITEKEY,
  '110110': TURNSTILE_ERROR_MESSAGES.INVALID_SITEKEY,
  '110200': TURNSTILE_ERROR_MESSAGES.INVALID_DOMAIN,
  '110420': TURNSTILE_ERROR_MESSAGES.INVALID_ACTION,
  '110430': TURNSTILE_ERROR_MESSAGES.INVALID_CDATA,
  '110500': TURNSTILE_ERROR_MESSAGES.UNSUPPORTED_BROWSER,
  '110510': TURNSTILE_ERROR_MESSAGES.INCONSISTENT_USER_AGENT,
  '200010': TURNSTILE_ERROR_MESSAGES.INVALID_CACHING,
  '200100': TURNSTILE_ERROR_MESSAGES.TIME_ERROR
};

const ERROR_CODES_PREFIXES = Object.keys(ERROR_MESSAGES_TO_PREFIXES_MAP);

const getErrorMessage = (errorCode: string): string => {
  if (!errorCode) return '';

  const prefix = ERROR_CODES_PREFIXES.find(
    (_prefix: string) => errorCode === _prefix || errorCode.startsWith(_prefix)
  );

  return prefix ? ERROR_MESSAGES_TO_PREFIXES_MAP[prefix] : '';
};

export default (errorCode: string): TurnstileErrorProps => ({
  'Error Code': errorCode,
  'Error Message': getErrorMessage(errorCode) || 'Undocumented Turnstile error'
});
